import React, { useEffect, useState, useCallback, useRef, useContext } from 'react';
import {
  Image,
  Platform,
  ScrollView,
  StyleSheet,
  Text,
  TouchableOpacity,
  View,
  Animated as ReactAnimated,
} from 'react-native';
import Animated, {
  measure,
  runOnUI,
  useAnimatedRef,
  useAnimatedStyle,
  useDerivedValue,
  useSharedValue,
  withTiming,
} from 'react-native-reanimated';
import { isTablet } from 'react-native-device-info';
import { EventRegister } from 'react-native-event-listeners';
import * as RootNavigation from '../../navigation/RootNavigation';
import colors from '../../resources/colors';
import images from '../../resources/images';
import AppContext from '../../navigation/Context';
import { useToast } from 'react-native-toast-notifications';
import { format } from 'date-fns';
import DataController from '../../controllers/DataController';
import FirebaseService from '../../controllers/FirebaseService';

const Separator = ({ imageMargin, highlight }) => (
  <View style={[highlight ? styles.separatorHighlight : styles.separator, { marginStart: imageMargin ? 60 : 30 }]} />
);

const DropdownSeparator = ({ highlight }) => (
  <View style={highlight ? styles.dropdownSeparatorHighlight : styles.dropdownSeparator} />
);

const GroupHeader = ({ text }) => {
  return (
    <Text style={styles.groupHeader} allowFontScaling={false}>
      {text.toUpperCase()}
    </Text>
  );
};

const Button = ({ text, icon, highlight }) => {
  const context = useContext(AppContext);
  const { days } = context;

  const toast = useToast();
  const tint = {
    tintColor: highlight ? colors.primary : colors.grey60,
  };

  const handlePress = () => {
    const route = RootNavigation.navigationRef.current?.getCurrentRoute();

    const closeAfterOpening = () => EventRegister.emit('menu/close-menu');

    switch (text) {
      // Dashboard
      case 'Home':
        if (route.name !== 'Dashboard') {
          !__DEV__ && FirebaseService.logEvent('menu_item_pressed', { button: 'Dashboard' });
          RootNavigation.navigate('Dashboard');
        }
        closeAfterOpening();
        break;
      case 'Today':
        !__DEV__ && FirebaseService.logEvent('menu_item_pressed', { button: 'Today' });
        const offset = new Date().getTimezoneOffset() * 60 * 1000;
        const time = new Date(new Date().valueOf() - offset);

        __DEV__ && console.log(format(time, 'yyyy-MM-dd'));
        const dayIndex = days.findIndex((_day) => _day.Date === DataController.formatDate(time));

        if (dayIndex) {
          !__DEV__ && FirebaseService.logEvent('day_viewed', { date: days[dayIndex].Date });
          RootNavigation.navigate('Day', { day: days[dayIndex], dayIndex });
          closeAfterOpening();
        }

        break;
      // Browse
      case 'Browse all dates':
        !__DEV__ && FirebaseService.logEvent('menu_item_pressed', { group: 'Browse', button: 'Browse all dates' });
        if (route.name !== 'Browse') {
          RootNavigation.navigate('Browse');
        }
        closeAfterOpening();
        break;
      case 'This week’s Lectionary':
        !__DEV__ && FirebaseService.logEvent('menu_item_pressed', { group: 'Browse', button: "This week's Lectionary" });
        if (route.name !== 'ThisWeeks') {
          RootNavigation.navigate('ThisWeeks');
        }
        closeAfterOpening();
        break;
      case 'Browse by Bible book':
        !__DEV__ && FirebaseService.logEvent('menu_item_pressed', { group: 'Browse', button: 'Browse by Bible book' });
        if (route.name !== 'Bible') {
          RootNavigation.navigate('Bible');
        }
        closeAfterOpening();
        break;
      case 'Recently viewed':
        !__DEV__ && FirebaseService.logEvent('menu_item_pressed', { group: 'Browse', button: 'Recently viewed' });
        if (route.name !== 'RecentlyViewed') {
          RootNavigation.navigate('RecentlyViewed');
        }
        closeAfterOpening();
        break;
      // My Services
      case 'Bookmarks':
        !__DEV__ && FirebaseService.logEvent('menu_item_pressed', { group: 'My Services', button: 'Bookmarks' });
        if (route.name !== 'Bookmarks') {
          RootNavigation.navigate('Bookmarks');
        }
        closeAfterOpening();
        break;
      case 'Notes':
        !__DEV__ && FirebaseService.logEvent('menu_item_pressed', { group: 'My Services', button: 'Notes' });
        if (route.name !== 'Notes') {
          RootNavigation.navigate('Notes');
        }
        closeAfterOpening();
        break;
      case 'Subscriptions':
      case 'Account':
        !__DEV__ && FirebaseService.logEvent('menu_item_pressed', { group: 'More', button: 'Subscriptions' });
        if (route.name !== 'Subscriptions') {
          RootNavigation.navigate('Subscriptions');
        }
        closeAfterOpening();
        break;
      case 'Settings':
        !__DEV__ && FirebaseService.logEvent('menu_item_pressed', { group: 'More', button: 'Settings' });
        if (route.name !== 'Settings') {
          RootNavigation.navigate('Settings');
        }
        closeAfterOpening();
        break;
      case 'About this app':
        !__DEV__ && FirebaseService.logEvent('menu_item_pressed', { group: 'More', button: 'About this app' });
        if (route.name !== 'About') {
          RootNavigation.navigate('About');
        }
        closeAfterOpening();
        break;
      case 'Notes on the Lectionary':
        !__DEV__ && FirebaseService.logEvent('menu_item_pressed', { group: 'More', button: 'Notes on the Lectionary' });
        if (route.name !== 'NotesOnLectionary') {
          RootNavigation.navigate('NotesOnLectionary');
        }
        closeAfterOpening();
        break;
      case 'Other Church of England apps':
        !__DEV__ && FirebaseService.logEvent('menu_item_pressed', { group: 'More', button: 'Other Church of England apps' });
        DataController.openLink('https://www.chpublishing.co.uk/apps');
        break;
      default:
        toast.show('Not implemented', { id: Date.now() });
        break;
    }
  };

  return (
    <View>
      <TouchableOpacity style={styles.button} activeOpacity={0.5} onPress={handlePress}>
        {icon && <Image style={[styles.icon, tint]} source={icon} />}
        <Text
          style={highlight ? styles.buttonTextSelected : styles.buttonText}
          allowFontScaling={false}>
          {text}
        </Text>
      </TouchableOpacity>
      <Separator imageMargin={icon !== undefined} highlight={highlight} />
    </View>
  );
};

const AnimatedButton = ({ text, icon, highlight, children }) => {
  const openAnim = useRef(new ReactAnimated.Value(0)).current;
  const disclosureAnim = useRef(new ReactAnimated.Value(0)).current;
  const [open, setOpen] = useState(false);
  const [dimensions, setDimensions] = useState(0);

  if (Platform.OS === 'android') {
    return (
      <AnimatedButtonAndroid text={text} icon={icon} highlight={highlight} children={children} />
    );
  }

  useEffect(() => {
    EventRegister.addEventListener('menu/menu-closed', () => {
      expandClose();
      setOpen(false);
    });

    return () => {
      EventRegister.removeEventListener('menu/menu-closed')
    };
  }, []);

  const expandOpen = (value) => {
    ReactAnimated.spring(openAnim, {
      toValue: dimensions,
      friction: 50,
      useNativeDriver: false,
    }).start();

    ReactAnimated.spring(disclosureAnim, {
      toValue: 90,
      friction: 50,
      useNativeDriver: false,
    }).start();
  };

  const expandClose = () => {
    ReactAnimated.spring(openAnim, {
      toValue: 0,
      friction: 50,
      useNativeDriver: false,
    }).start();

    ReactAnimated.spring(disclosureAnim, {
      toValue: 0,
      friction: 50,
      useNativeDriver: false,
    }).start();
  };

  const handlePress = () => {
    if (open) {
      expandClose();
    } else {
      expandOpen();
    }

    setOpen(!open);
  };

  const measureDimensions = (event) => {
    const _height = event.nativeEvent.layout.height;
    setDimensions(_height);
  };

  const tint = {
    tintColor: highlight ? colors.primary : colors.grey60,
  };

  const heightStyles = {
    overflow: Platform.OS === 'ios' ? 'scroll' : 'hidden',
    height: openAnim,
  };

  const animatedStyles = {
    transform: [
      {
        rotate: disclosureAnim.interpolate({
          inputRange: [0, 90],
          outputRange: ['0deg', '90deg'],
        }),
      },
    ],
  };

  return (
    <ReactAnimated.View key={text}>
      <TouchableOpacity style={styles.button} activeOpacity={0.5} onPress={handlePress}>
        <Image style={[styles.icon, tint]} source={icon} />
        <Text
          style={highlight ? styles.buttonTextSelected : styles.buttonText}
          allowFontScaling={false}>
          {text}
        </Text>
        <ReactAnimated.View style={animatedStyles}>
          <Image style={[styles.dropdown, tint]} source={images.Button_Expand_Grey} />
        </ReactAnimated.View>
      </TouchableOpacity>
      <Separator highlight={highlight} />
      <ReactAnimated.View style={heightStyles}>
        <View onLayout={(event) => measureDimensions(event)}>{children}</View>
      </ReactAnimated.View>
    </ReactAnimated.View>
  );
};

const AnimatedButtonAndroid = ({ text, icon, highlight, children }) => {
  const ref = useAnimatedRef();
  const open = useSharedValue(false);
  const offset = useSharedValue(0);
  const height = useSharedValue(0);
  const transition = useDerivedValue(() => {
    return open.value ? withTiming(1) : withTiming(0);
  });

  const animatedStyles = useAnimatedStyle(() => {
    return {
      transform: [{ rotate: `${offset.value}deg` }],
    };
  });

  const heightStyles = useAnimatedStyle(() => {
    return {
      height: 1 + transition.value * height.value,
      opacity: transition.value === 0 ? 0 : 1,
      overflow: Platform.OS === 'android' ? 'hidden' : 'visible',
    };
  });

  const tint = {
    tintColor: highlight ? colors.primary : colors.grey60,
  };

  const handlePress = () => {
    if (height.value === 0) {
      runOnUI(() => {
        'worklet';
        height.value = measure(ref).height;
      })();
    }

    offset.value = withTiming(open.value ? 0 : 90);
    open.value = !open.value;
  };

  return (
    <Animated.View key={text}>
      <TouchableOpacity style={styles.button} activeOpacity={0.5} onPress={handlePress}>
        <Image style={[styles.icon, tint]} source={icon} />
        <Text
          style={highlight ? styles.buttonTextSelected : styles.buttonText}
          allowFontScaling={false}>
          {text}
        </Text>
        <Animated.View style={animatedStyles}>
          <Image style={[styles.dropdown, tint]} source={images.Button_Expand_Grey} />
        </Animated.View>
      </TouchableOpacity>
      <Separator highlight={highlight} />
      <Animated.View style={heightStyles}>
        <View ref={ref}>{children}</View>
      </Animated.View>
    </Animated.View>
  );
};

const DropdownButton = ({ text, highlight }) => {
  const toast = useToast();

  const buttonPressed = () => {
    const route = RootNavigation.navigationRef.current?.getCurrentRoute();

    switch (text) {
      case 'Browse all dates':
        if (route.name !== 'Browse') {
          RootNavigation.navigate('Browse');
        }
        EventRegister.emit('menu/close-menu');
        break;
      case 'Browse by Bible book':
        if (route.name !== 'Bible') {
          RootNavigation.navigate('Bible');
        }
        EventRegister.emit('menu/close-menu');
        break;
      default:
        toast.show('Not implemented', { id: Date.now() });
        break;
    }
  };

  return (
    <View>
      <TouchableOpacity style={styles.dropdownButton} activeOpacity={0.5} onPress={buttonPressed}>
        <Text
          style={highlight ? styles.buttonTextSelected : styles.buttonText}
          allowFontScaling={false}>
          {text}
        </Text>
      </TouchableOpacity>
      <DropdownSeparator highlight={highlight} />
    </View>
  );
};

const MenuScreen = ({ navigation }) => {
  const context = useContext(AppContext);

  useEffect(() => {
    navigation.setOptions({
      headerLeft: () => (
        <TouchableOpacity style={styles.buttonMenu} onPress={closeMenu}>
          <Image style={styles.menuIcon} source={images.Button_Close} />
        </TouchableOpacity>
      ),
      headerTitle: () => <View />,
      headerRight: () => <View />,
      headerStyle: {
        backgroundColor: colors.grey20,
        shadowRadius: 0,
        shadowOffset: {
          height: 0,
        },
        shadowOpacity: 0,
        height: Platform.OS === 'web' ? 44 : undefined,
      },
    });
  }, [closeMenu, navigation]);

  const closeMenu = useCallback(() => {
    EventRegister.emit('menu/close-menu', true);
  }, []);


  return (
    <View style={styles.container}>
      <ScrollView
        contentContainerStyle={styles.scrollViewContainer}
        indicatorStyle="black"
        scrollIndicatorInsets={{ right: 1 }}
        bounces={false}
        stickyHeaderIndices={[0]}>
        <View style={styles.lectionaryBanner}>
          <Image
            style={styles.lectionaryIcon}
            source={images.Menu_Banner}
          />
        </View>
        <Button
          text="Home"
          highlight={true}
        />
        <Button
          text="Today"
        />
        <GroupHeader text="Browse" />
        <Button text="Browse all dates" />
        <Button
          text="This week’s Lectionary"
        />
        <Button
          text="Browse by Bible book"
        />
        <Button text="Recently viewed" />
        <GroupHeader text="My Services" />
        <Button
          text="Bookmarks"
        />
        <Button
          text="Notes"
        />
        <GroupHeader text="More" />
        <Button
          text={Platform.OS === 'web' ? 'Account' : 'Subscriptions'}
        />
        <Button
          text="Notes on the Lectionary"
        />
        <Button text="Settings" />
        <Button text="About this app" />
        <Button
          text="Other Church of England apps"
        />
      </ScrollView>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: colors.accentWhite,
  },
  scrollViewContainer: {
    paddingBottom: 54,
  },
  lectionaryBanner: {
    height: 72,
    flexDirection: 'row',
    paddingStart: 26,
    backgroundColor: colors.primary,
    alignItems: 'center',
  },
  lectionaryIcon: {
    width: 167,
    height: 28,
  },
  lectionaryText: {
    fontFamily: 'FiraSans-Bold',
    fontSize: 28,
    fontWeight: Platform.OS === 'ios' ? 'bold' : 'normal',
    fontStyle: 'normal',
    lineHeight: 34,
    letterSpacing: 0.2,
    color: colors.accentWhite,
  },
  title: {
    fontFamily: 'FiraSans-SemiBold',
    fontSize: 17,
    fontWeight: Platform.OS === 'ios' ? '600' : 'normal',
    lineHeight: 24,
    letterSpacing: -0.1,
    textAlign: 'center',
    color: colors.primary,
  },
  buttonMenu: {
    width: 44,
    height: 44,
    marginStart: 4,
  },
  menuIcon: {
    width: 44,
    height: 44,
  },
  button: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    height: 60,
    paddingHorizontal: 30,
    // paddingStart: 30,
    // paddingEnd: 30,
  },
  groupHeader: {
    fontFamily: 'FiraSans-Bold',
    fontSize: 12,
    fontWeight: Platform.OS === 'ios' ? 'bold' : 'normal',
    fontStyle: 'normal',
    lineHeight: 16,
    letterSpacing: 0.5,
    color: '#A9ACB0',
    marginTop: 24,
    marginBottom: 2,
    marginStart: 30,
  },
  icon: {
    height: 44,
    width: 44,
    marginEnd: 5,
  },
  buttonTextSelected: {
    flex: 1,
    fontFamily: 'FiraSans-Bold',
    fontSize: 17,
    fontWeight: Platform.OS === 'ios' ? 'bold' : 'normal',
    fontStyle: 'normal',
    lineHeight: 24,
    letterSpacing: 0.1,
    color: colors.primary,
  },
  buttonText: {
    flex: 1,
    fontFamily: 'FiraSans-SemiBold',
    fontSize: 17,
    fontWeight: Platform.OS === 'ios' ? '600' : 'normal',
    fontStyle: 'normal',
    lineHeight: 24,
    letterSpacing: 0.1,
    color: colors.grey70,
  },
  separator: {
    height: 1,
    backgroundColor: colors.grey40,
    marginStart: 60,
    marginEnd: 30,
  },
  separatorHighlight: {
    height: 2,
    backgroundColor: colors.primary,
    marginStart: 60,
    marginEnd: 30,
  },
  dropdown: {
    width: 44,
    height: 44,
  },
  dropdownSeparatorHighlight: {
    height: 2,
    backgroundColor: colors.primary,
    marginStart: 74,
    marginEnd: 30,
  },
  dropdownSeparator: {
    height: 1,
    backgroundColor: colors.grey30,
    marginStart: 74,
    marginEnd: 30,
  },
  dropdownButton: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    minHeight: 18,
    paddingStart: 74,
    paddingEnd: 30,
    paddingVertical: 21,
  },
});

export default MenuScreen;
